import { useState, useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import { Ban } from "lucide-react"
import ToolShell from "../components/ToolShell"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import CTABox from "../components/CTABox"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"

const SCENARIOS = [5, 8, 10, 15]

export default function VacancyRateCostCalculatorPage() {
  const { tool, currency } = useOutletContext<{ tool: ToolDef, currency: string }>()

  const [monthlyRent, setMonthlyRent] = useState("")
  const [vacancyRate, setVacancyRate] = useState(8)
  const [fixedCosts, setFixedCosts] = useState("")

  const calcCost = (rent: number, rate: number, fixed: number) => {
    const annualIncome = rent * 12
    const vacancyLoss = annualIncome * (rate / 100)
    const vacancyMonths = 12 * (rate / 100)
    const costsDuringVacancy = fixed * vacancyMonths
    const totalVacancyCost = vacancyLoss + costsDuringVacancy
    const effectiveMonthly = (annualIncome - totalVacancyCost) / 12
    return { vacancyLoss, costsDuringVacancy, totalVacancyCost, effectiveMonthly }
  }

  const results = useMemo(() => {
    const rent = parseFloat(monthlyRent)
    if (!rent) return null
    const fixed = parseFloat(fixedCosts) || 0
    const main = calcCost(rent, vacancyRate, fixed)
    const savingPer1Pct = rent * 12 * 0.01
    return { ...main, savingPer1Pct, rent, fixed }
  }, [monthlyRent, vacancyRate, fixedCosts])

  const fmt = (n: number) => `${currency}${Math.round(n).toLocaleString()}`

  return (
    <div className="space-y-8">
      <ToolShell>
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Input Panel */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Property Details</p>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Monthly Rent</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                  <input
                    type="number"
                    min="0"
                    value={monthlyRent}
                    onChange={e => setMonthlyRent(e.target.value)}
                    placeholder="e.g. 1200"
                    className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Annual Vacancy Rate — <span className="font-semibold text-primary">{vacancyRate}%</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="30"
                  step="1"
                  value={vacancyRate}
                  onChange={e => setVacancyRate(Number(e.target.value))}
                  className="w-full accent-primary"
                />
                <div className="flex justify-between text-xs text-slate-400 mt-1">
                  <span>0%</span><span>30%</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Fixed Monthly Costs During Vacancy</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                  <input
                    type="number"
                    min="0"
                    value={fixedCosts}
                    onChange={e => setFixedCosts(e.target.value)}
                    placeholder="e.g. 800 (mortgage, insurance)"
                    className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>
            </div>

            {/* Result Panel */}
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Results</p>

              {!results ? (
                <p className="text-slate-400 text-sm text-center py-10">Enter monthly rent to see vacancy costs</p>
              ) : (
                <>
                  <div className="text-center py-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Annual Cost of Vacancy</p>
                    <p className="text-5xl font-extrabold text-red-500">{fmt(results.totalVacancyCost)}</p>
                  </div>

                  <div className="border-t border-primary/10 pt-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Lost Rental Income</span>
                      <span className="font-semibold text-slate-900">{fmt(results.vacancyLoss)}</span>
                    </div>
                    {results.fixed > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Costs During Vacancy</span>
                        <span className="font-semibold text-slate-900">{fmt(results.costsDuringVacancy)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Effective Monthly Income</span>
                      <span className="font-semibold text-green-600">{fmt(results.effectiveMonthly)}</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-primary/10 pt-3">
                      <span className="text-slate-600">Saving per 1% vacancy reduction</span>
                      <span className="font-semibold text-primary">{fmt(results.savingPer1Pct)}/yr</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Scenario Comparison */}
          {results && (
            <div className="mt-6 border-t border-slate-200 pt-6">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">Vacancy Scenario Comparison</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {SCENARIOS.map(rate => {
                  const s = calcCost(results.rent, rate, results.fixed)
                  const isActive = rate === vacancyRate
                  return (
                    <button
                      key={rate}
                      onClick={() => setVacancyRate(rate)}
                      className={`rounded-xl border p-3 text-left transition-all ${isActive ? "border-primary bg-primary/5" : "border-slate-200 bg-white hover:border-primary/40"}`}
                    >
                      <p className={`text-lg font-bold ${isActive ? "text-primary" : "text-slate-900"}`}>{rate}% vacancy</p>
                      <p className="text-xs text-slate-500 mt-1">Annual loss: <span className="text-red-500 font-semibold">{fmt(s.totalVacancyCost)}</span></p>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </ToolShell>

      <FormulaBox
        formula={`Vacancy Loss = Annual Rent × (Vacancy Rate ÷ 100)\nVacancy Months = 12 × (Vacancy Rate ÷ 100)\nCosts During Vacancy = Fixed Monthly Costs × Vacancy Months\n\nTotal Vacancy Cost = Vacancy Loss + Costs During Vacancy\nEffective Monthly Income = (Annual Rent − Total Cost) ÷ 12`}
        description="Most landlords underestimate vacancy costs because they only count lost rent — but fixed costs (mortgage, insurance) continue even when the property is empty."
      />

      <ToolFAQ
        items={[
          {
            q: "What is a typical vacancy rate for UK rental properties?",
            a: "A typical void period is 1–3 weeks per year, equating to roughly 2–6% vacancy. Many investors budget 5–8% to be conservative. High-demand areas or purpose-built student accommodation can have much lower void rates.",
          },
          {
            q: "How do I reduce my vacancy rate?",
            a: "Start re-marketing at least 6–8 weeks before a tenancy ends. Keep the property well-maintained to attract good tenants quickly. Price competitively with local market rates. Offering incentives like flexible move-in dates or including some utilities can also reduce gaps.",
          },
          {
            q: "Should I include fixed costs in my vacancy calculation?",
            a: "Yes — this is critical. Mortgage payments, insurance, service charges, and ground rent continue even when the property is empty. Including these gives a true picture of what vacancy actually costs you rather than just the lost rent.",
          },
        ]}
      />

      <CTABox
        icon={Ban}
        heading="Want to minimize void periods?"
        body={tool.ctaText}
        ctaLabel="Start free"
        ctaHref="/register"
      />

      <RelatedToolsList relatedSlugs={tool.related} />
    </div>
  )
}
